import { createContext, useContext, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import { setCompanyTimezone } from "@/lib/format-utils";

export const DEFAULT_TAGLINE = "Public adjusting, simplified";

// The previous long default, still stored on firm rows created before DEFAULT_TAGLINE was
// shortened. The sidebar swaps this legacy value for the shorter subtitle (see sidebar.tsx).
export const LEGACY_DEFAULT_TAGLINE = "Public adjusting operations for independent firms";

export interface IBrandingSettings {
  companyName: string;
  siteName: string | null;
  displayName: string;
  tagline: string;
  logoUrl: string | null;
  logoSmallUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  timezone?: string;
}

const defaultBranding: IBrandingSettings = {
  companyName: "ClaimHive",
  siteName: null,
  displayName: "ClaimHive",
  tagline: DEFAULT_TAGLINE,
  logoUrl: null,
  logoSmallUrl: null,
  faviconUrl: null,
  primaryColor: "#0f172a", // Slate 900
  accentColor: "#3b82f6",  // Blue 500
  timezone: "America/New_York",
};

const BrandingContext = createContext<IBrandingSettings>(defaultBranding);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data: branding } = useQuery<IBrandingSettings>({
    queryKey: ["branding"],
    queryFn: async () => {
      try {
        const res = await fetch("/api/branding");
        if (!res.ok) throw new Error("Failed to fetch branding");
        return res.json();
      } catch (error) {
        console.warn("Using default branding due to error:", error);
        return defaultBranding;
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const rawSettings = branding || defaultBranding;
  const settings: IBrandingSettings = {
    ...rawSettings,
    displayName: rawSettings.siteName || rawSettings.companyName,
  };

  // Update document title and favicon
  useEffect(() => {
    if (settings) {
      document.title = settings.displayName || "ClaimHive";
      
      if (settings.timezone) {
        setCompanyTimezone(settings.timezone);
      }
      
      // Use faviconUrl if available, otherwise logoSmallUrl, otherwise default
      const faviconUrl = settings.faviconUrl || settings.logoSmallUrl || "/favicon.ico";
      
      const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (faviconLink) {
        faviconLink.href = faviconUrl;
      } else {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.href = faviconUrl;
        document.head.appendChild(newLink);
      }
    
      // Set CSS variables for colors if needed (optional, for advanced theming)
      if (settings.primaryColor) {
        document.documentElement.style.setProperty('--primary-brand', settings.primaryColor);
      }
      if (settings.accentColor) {
        document.documentElement.style.setProperty('--accent-brand', settings.accentColor);
      }
    }
  }, [settings]);

  return (
    <BrandingContext.Provider value={settings}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
